import { Injectable } from '@angular/core';

export type DashboardTooltipKey =
  | 'filterByDate'
  | 'filterByProvince'
  | 'filterByLanguage'
  | 'downloadButton'
  | 'learnerDistributionByCountry'
  | 'learnerBreakdownByProvince'
  | 'detailedStatisticsProvince'
  | 'raceBreakdownOfLearners'
  | 'genderBreakdownOfLearners'
  | 'learnerLanguageSelection'
  | 'detailedStatisticsLanguage'
  | 'demographicIncomeBracket'
  | 'ageBreakdownOfLearners'
  | 'learnerEngagementByLocationType'
  | 'userRole'
  | 'totalRegisteredUsers'
  | 'startedLearning'
  | 'completedLearning'
  | 'completionRate'
  | 'prePostQuizAnalysis'
  | 'questionByQuestionAnalysis'
  | 'overallKnowledgeGain'
  | 'keyInsights'
  | 'learningContentProgressOverview'
  | 'preTestAssessmentOverview'
  | 'postTestAssessmentOverview'
  | 'overallEngagementRate'
  | 'dropOffRate'
  | 'easeOfUse'
  | 'contentRelevance'
  | 'engagementFeedback'
  | 'experience'
  | 'improvementFeedback'
  | 'feedbackRatingOverTime'
  | 'totalResponses'
  | 'averageRating'
  | 'participationRate'
  | 'engagementTrend';

export interface DashboardTooltip {
  label: string;
  text: string;
}

const DASHBOARD_TOOLTIPS: Readonly<Record<DashboardTooltipKey, DashboardTooltip>> = {
  filterByDate: {
    label: 'Filter by Date',
    text: 'Filter all data to show a specific time period.',
  },
  filterByProvince: {
    label: 'Filter by Province',
    text: 'Show data for a specific South African province only.',
  },
  filterByLanguage: {
    label: 'Filter by Language',
    text: 'Show data filtered by the language learners selected.',
  },
  downloadButton: {
    label: 'Download Button',
    text: 'Download this report as a file to your device.',
  },
  learnerDistributionByCountry: {
    label: 'Learner Distribution by Country',
    text: 'Shows which countries your learners are from.',
  },
  learnerBreakdownByProvince: {
    label: 'Learner Breakdown by Province',
    text: 'Shows how many learners are in each South African province.',
  },
  detailedStatisticsProvince: {
    label: 'Detailed Statistics (Province)',
    text: 'A full breakdown of learner numbers per province.',
  },
  raceBreakdownOfLearners: {
    label: 'Race Breakdown of Learners',
    text: 'Shows the racial breakdown of learners. This data is self-reported and may be incomplete.',
  },
  genderBreakdownOfLearners: {
    label: 'Gender Breakdown of Learners',
    text: 'Shows how learners identified their gender during onboarding.',
  },
  learnerLanguageSelection: {
    label: 'Learner Language Selection',
    text: 'Shows which language each learner chose to learn in.',
  },
  detailedStatisticsLanguage: {
    label: 'Detailed Statistics (Language)',
    text: 'A full list of learner counts per language selected.',
  },
  demographicIncomeBracket: {
    label: 'Demographic Income Bracket',
    text: 'Shows the income ranges learners selected during onboarding. This data is self-reported.',
  },
  ageBreakdownOfLearners: {
    label: 'Age Breakdown of Learners',
    text: 'Shows the age groups of your learners.',
  },
  learnerEngagementByLocationType: {
    label: 'Learner Engagement by Location Type',
    text: 'Shows whether learners are based in urban, rural, or township areas.',
  },
  userRole: {
    label: 'User Role',
    text: 'Shows the roles learners selected when they signed up (e.g. Teacher, TA, ECD).',
  },
  totalRegisteredUsers: {
    label: 'Total Registered Users',
    text: 'The total number of people who have signed up, by giving us their Name, Surname and Email.',
  },
  startedLearning: {
    label: 'Started Learning',
    text: 'The number of learners who have begun at least one course.',
  },
  completedLearning: {
    label: 'Completed Learning',
    text: 'Learners who finished all 5 modules, passed the post-learning quiz, and received their certificate.',
  },
  completionRate: {
    label: 'Completion Rate',
    text: 'The percentage of certified learners compared to those who are still in progress or have not completed yet.',
  },
  prePostQuizAnalysis: {
    label: 'Pre/Post Quiz Analysis',
    text: 'Compares how learners scored before and after completing a course. Higher post-quiz scores mean learners improved.',
  },
  questionByQuestionAnalysis: {
    label: 'Question by Question Analysis',
    text: 'Shows how learners answered each quiz question before and after learning. Use this to spot which topics need more work.',
  },
  overallKnowledgeGain: {
    label: 'Overall Knowledge Gain',
    text: 'The average improvement in quiz scores across all learners.',
  },
  keyInsights: {
    label: 'Key Insights',
    text: 'A summary of the most important learning trends from this period.',
  },
  learningContentProgressOverview: {
    label: 'Learning Content Progress Overview',
    text: 'Shows how many learners enrolled in, completed, and passed each module.',
  },
  preTestAssessmentOverview: {
    label: 'Pre-Test (Assessment Overview)',
    text: 'The average score learners got before starting the course.',
  },
  postTestAssessmentOverview: {
    label: 'Post-Test (Assessment Overview)',
    text: 'The average score learners got after completing the course.',
  },
  overallEngagementRate: {
    label: 'Overall Engagement Rate',
    text: 'How actively learners are interacting with the bot and course content.',
  },
  dropOffRate: {
    label: 'Drop-off Rate',
    text: 'The percentage of learners who stopped before finishing a course. A lower number is better.',
  },
  easeOfUse: {
    label: 'Ease of Use',
    text: 'How easy learners said it was to use and navigate the bot.',
  },
  contentRelevance: {
    label: 'Content Relevance',
    text: 'How useful and relevant learners found the course content.',
  },
  engagementFeedback: {
    label: 'Engagement',
    text: 'How motivated and engaged learners felt while using the bot.',
  },
  experience: {
    label: 'Experience',
    text: 'How learners rated their overall experience with the bot.',
  },
  improvementFeedback: {
    label: 'Improvement',
    text: 'Areas learners felt could be better. Use this to guide future updates.',
  },
  feedbackRatingOverTime: {
    label: 'Feedback Rating Over Time',
    text: 'Tracks how learner satisfaction ratings have changed month by month.',
  },
  totalResponses: {
    label: 'Total Responses',
    text: 'Learners who opted in and submitted a feedback rating, compared to those who completed all 5 modules but opted out of the survey.',
  },
  averageRating: {
    label: 'Average Rating',
    text: 'The combined average star rating from all learner feedback.',
  },
  participationRate: {
    label: 'Participation Rate',
    text: 'Calculated against the total number of learners who opted in and opted out of the survey.',
  },
  engagementTrend: {
    label: 'Engagement Trend',
    text: 'Shows whether learner participation is growing or declining over time.',
  },
};

@Injectable({
  providedIn: 'root',
})
export class TooltipService {
  get(key: DashboardTooltipKey): DashboardTooltip {
    return DASHBOARD_TOOLTIPS[key];
  }

  text(key: DashboardTooltipKey): string {
    return DASHBOARD_TOOLTIPS[key].text;
  }

  label(key: DashboardTooltipKey): string {
    return DASHBOARD_TOOLTIPS[key].label;
  }

  all(): Readonly<Record<DashboardTooltipKey, DashboardTooltip>> {
    return DASHBOARD_TOOLTIPS;
  }
}

